import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  TrendingUp, Globe, MessageSquare, Star, DollarSign,
  ClipboardList, Inbox, CheckCircle2, ArrowRight,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';

const BENEFITS = [
  {
    icon: TrendingUp,
    title: 'Get More Patients',
    desc: 'Receive real patient enquiries directly from verified patients looking for your services.',
  },
  {
    icon: Globe,
    title: 'Reach Local & International Patients',
    desc: 'Expand beyond walk-ins. Attract patients from across Vietnam and international visitors.',
  },
  {
    icon: MessageSquare,
    title: 'Direct Communication',
    desc: 'Connect with patients instantly via WhatsApp or Zalo — no middlemen, no delays.',
  },
  {
    icon: Star,
    title: 'Build Trust',
    desc: 'Get a verified clinic profile with real patient reviews that build lasting credibility.',
  },
  {
    icon: DollarSign,
    title: 'Reduce Advertising Costs',
    desc: 'No expensive ads needed. Patients find you organically through VietClinics search.',
  },
];

const STEPS = [
  {
    icon: ClipboardList,
    step: '01',
    title: 'Register your clinic',
    desc: 'Complete a simple onboarding form with your clinic details, services, and team.',
  },
  {
    icon: Inbox,
    step: '02',
    title: 'Receive patient enquiries',
    desc: 'Verified patients discover your profile and send booking requests directly to you.',
  },
  {
    icon: CheckCircle2,
    step: '03',
    title: 'Confirm bookings and grow',
    desc: 'Confirm appointments, collect reviews, and watch your patient base expand.',
  },
];

const ForClinicsPage = () => {
  const { contactInfo } = useContactInfo();
  const whatsappHref = getWhatsAppHref(contactInfo, 'Hi VietClinics, I would like to learn more about your clinic partnership program.');

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Clinic Partnership Information | VietClinics</title>
        <meta name="description" content="Learn about working with VietClinics on patient enquiry coordination in Vietnam. Share your clinic details to discuss services and partnership arrangements." />
        <link rel="canonical" href="https://vietclinics.com/for-clinics" />
      </Helmet>
      <Header />

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-br from-[#1E5BA8] via-[#1FA7A1] to-[#26A9E0]">
        <div className="container mx-auto px-4 text-center">
          <span className="inline-block text-white/70 text-sm font-semibold tracking-widest uppercase mb-4">
            Partner with us
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Grow Your Clinic<br className="hidden md:block" /> with VietClinics
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10">
            Join Vietnam's trusted healthcare platform and receive patient enquiries directly.
            Increase your visibility, reduce marketing costs, and grow your practice.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/clinic-registration"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#1FA7A1] font-bold rounded-xl text-base hover:bg-gray-50 shadow-xl transition-all"
              data-testid="hero-register-btn"
            >
              Register Your Clinic
              <ArrowRight className="h-4 w-4" />
            </Link>
            {whatsappHref && (
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#25D366] text-white font-bold rounded-xl text-base hover:bg-[#1fb355] shadow-xl transition-all"
                data-testid="hero-whatsapp-btn"
              >
                <svg className="h-5 w-5 fill-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Contact Us on WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ── Benefits ───────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Partner with VietClinics?
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              We connect your clinic with patients who are actively searching for your treatments.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {BENEFITS.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={i}
                className="bg-gray-50 rounded-2xl p-6 hover:shadow-sm transition-shadow"
                data-testid={`benefit-card-${i}`}
              >
                <div className="w-12 h-12 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="h-6 w-6 text-[#1FA7A1]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}

            {/* CTA card — fills 6th slot in 3-col grid */}
            <div className="bg-[#1FA7A1] rounded-2xl p-6 flex flex-col justify-between sm:col-span-2 lg:col-span-1">
              <div>
                <h3 className="font-bold text-white text-lg mb-2">Ready to get started?</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Join hundreds of clinics already growing with VietClinics.
                </p>
              </div>
              <Link
                to="/clinic-registration"
                className="mt-6 inline-flex items-center gap-2 bg-white text-[#1FA7A1] font-semibold rounded-xl px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors w-fit"
              >
                Register Now <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Getting started takes less than 10 minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {STEPS.map(({ icon: Icon, step, title, desc }, i) => (
              <div key={i} className="relative text-center" data-testid={`step-${i + 1}`}>
                {/* Connector line between steps */}
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[calc(50%+2.5rem)] w-[calc(100%-5rem)] h-px bg-[#1FA7A1]/20" />
                )}
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-5 relative z-10">
                  <Icon className="h-7 w-7 text-[#1FA7A1]" />
                </div>
                <span className="text-xs font-bold text-[#1FA7A1] tracking-widest uppercase">Step {step}</span>
                <h3 className="font-bold text-gray-900 mt-1 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-[#1E5BA8] to-[#1FA7A1]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Ready to Grow Your Practice?
          </h2>
          <p className="text-white/80 mb-10 max-w-xl mx-auto">
            Start receiving patient enquiries. Registration is quick and free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/clinic-registration"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#1FA7A1] font-bold rounded-xl text-base hover:bg-gray-50 shadow-xl transition-all"
              data-testid="cta-register-btn"
            >
              Register Your Clinic
              <ArrowRight className="h-4 w-4" />
            </Link>
            {whatsappHref && (
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#25D366] text-white font-bold rounded-xl text-base hover:bg-[#1fb355] shadow-xl transition-all"
                data-testid="cta-whatsapp-btn"
              >
                <svg className="h-5 w-5 fill-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Contact Us on WhatsApp
              </a>
            )}
          </div>

          <p className="text-white/60 text-sm mt-10 italic">
            Early partner clinics receive free onboarding and priority placement.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForClinicsPage;
